// src/pages/msme/SHEETALThink5WhysRoles.jsx
import React, { useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faChevronDown, 
  faChevronUp, 
  faDownload, 
  faCopy,
  faStar
} from "@fortawesome/free-solid-svg-icons";
import jsPDF from "jspdf";

/**
 * SHEETALThink5WhysRoles.jsx
 * Mobile-first, responsive, dark themed SHEETAL Ecosystem Roles view.
 *
 * Usage:
 *  - Requires Tailwind CSS and FontAwesome (react-fontawesome).
 *  - Place in your routes/pages as appropriate.
 */

const ecosystemRoles = [
  {
    id: "role-1",
    emoji: "👤",
    role: "SHEETAL Owner",
    function: "Core Business Operator",
    responsibilities: [
      "Strategy, Operations, Compliance, Productivity, Growth Decisions"
    ],
    interaction: "Central, interacts with all roles",
    color: "bg-emerald-700"
  },
  {
    id: "role-2",
    emoji: "🏦",
    role: "Bank / Financial Institution",
    function: "Funding & Financial Support",
    responsibilities: [
      "Loans, Credit Lines, Financial Advisory, Risk Assessment"
    ],
    interaction: "Provides capital and advisory",
    color: "bg-indigo-700"
  },
  {
    id: "role-3",
    emoji: "🔄",
    role: "ARC (Asset Recovery Company)",
    function: "NPA & Asset Management",
    responsibilities: [
      "Recovery Solutions, Debt Restructuring, Portfolio Management"
    ],
    interaction: "Supports SHEETAL in distressed situations",
    color: "bg-amber-700"
  },
  {
    id: "role-4",
    emoji: "🏛️",
    role: "Government / Regulator",
    function: "Policy & Compliance Oversight",
    responsibilities: [
      "Regulatory Guidelines, Subsidies, Schemes (PMMY, SAKSHAM), Tax Incentives"
    ],
    interaction: "Mandates compliance and provides schemes",
    color: "bg-violet-700"
  },
  {
    id: "role-5",
    emoji: "👨‍💼",
    role: "Professional Advisors",
    function: "Accounting, Legal, HR & Compliance",
    responsibilities: [
      "Financial Audits, Legal Support, HR & Labour Compliance, Tax Planning"
    ],
    interaction: "Provides expert guidance",
    color: "bg-pink-700"
  },
  {
    id: "role-6",
    emoji: "🛍️",
    role: "Boutique Advisors",
    function: "Specialized SME Consulting",
    responsibilities: [
      "Niche Industry Advisory, Market Entry Strategy, Business Process Optimization"
    ],
    interaction: "Delivers focused advisory",
    color: "bg-yellow-700"
  },
  {
    id: "role-7",
    emoji: "🧭",
    role: "Think5Whys Guide",
    function: "Transformation & Productivity Coach",
    responsibilities: [
      "Kaizen, DeepTech Adoption, Self-Assessment, Productivity Optimization, Decision Support"
    ],
    interaction: "Guides SHEETAL owner through structured growth path",
    color: "bg-cyan-700"
  },
  {
    id: "role-8",
    emoji: "🧠",
    role: "Mentors / Experts Network",
    function: "Knowledge & Innovation Support",
    responsibilities: [
      "Innovation Labs, Workshops, Case Studies, Peer Learning"
    ],
    interaction: "Provides experience-based insights",
    color: "bg-lime-700"
  },
  {
    id: "role-9",
    emoji: "🌐",
    role: "Tech & Digital Partners",
    function: "Technology Enablement",
    responsibilities: [
      "Digital Tools, Platforms, Marketplace Access, Metaverse & AI Integration"
    ],
    interaction: "Enables automation and digital growth",
    color: "bg-rose-700"
  }
];

const cardHeaderClass =
  "flex items-center gap-3 p-3 rounded-lg text-sm font-semibold text-white";

const SHEETALThink5WhysRoles = () => {
  const [openRoleId, setOpenRoleId] = useState(null);
  const [copied, setCopied] = useState(false);
  const pdfRef = useRef();

  const toggleRole = (id) => setOpenRoleId(prev => (prev === id ? null : id));

  const handleCopyRole = (role) => {
    const text = `${role.role} — ${role.function}\nResponsibilities:\n- ${role.responsibilities.join("\n- ")}\nInteraction: ${role.interaction}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }).catch(() => {});
  };

  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    doc.setFontSize(14);
    doc.text("SAKSHAM SHEETAL — Ecosystem Roles", 40, 40);
    let y = 70;
    ecosystemRoles.forEach((r, idx) => {
      doc.setFontSize(12);
      doc.text(`${idx + 1}. ${r.role} ${r.emoji}`, 40, y);
      y += 18;
      doc.setFontSize(10);
      doc.text(`Function: ${r.function}`, 60, y);
      y += 14;
      doc.text("Responsibilities:", 60, y);
      y += 14;
      r.responsibilities.forEach(resp => {
        if (y > 750) { doc.addPage(); y = 40; }
        doc.text(`• ${resp}`, 80, y);
        y += 12;
      });
      y += 6;
      doc.text(`Interaction: ${r.interaction}`, 60, y);
      y += 24;
    });
    doc.save("msme-ecosystem-roles.pdf");
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4">
      {/* Header */}
      <header className="mb-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg text-start sm:text-xl font-bold">SHEETAL Ecosystem Roles</h1>
            <p className="text-sm text-gray-200 mt-1 max-w-xl">
              Key stakeholders supporting millions of micro-enterprises and startups — highlighting roles, responsibilities, and interactions with SHEETAL Owners.
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <button
              onClick={handleExportPDF}
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 hover:bg-gray-700 text-sm"
              title="Export full roles as PDF"
            >
              <FontAwesomeIcon icon={faDownload} /> Export PDF
            </button>
          </div>
        </div>
      </header>

      {/* Grid of role cards */}
      <main ref={pdfRef} className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {ecosystemRoles.map(role => {
          const isOpen = openRoleId === role.id;
          return (
            <article key={role.id} className="relative">
              <div className={`rounded-2xl overflow-hidden shadow-lg ${role.color} bg-opacity-90`}>
                {/* Header */}
                <div className={`${cardHeaderClass}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-2xl">{role.emoji}</div>
                        <div>
                          <div className="text-sm font-bold leading-tight">{role.role}</div>
                          <div className="text-xs text-gray-200/90 mt-0.5">{role.function}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleRole(role.id); }}
                          aria-expanded={isOpen}
                          className="p-2 rounded-md bg-white bg-opacity-10 hover:bg-opacity-20"
                          title={isOpen ? "Collapse" : "Expand"}
                        >
                          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="p-4 bg-gray-900/20">
                  <div className={`transition-[max-height] duration-300 ease-in-out overflow-hidden ${isOpen ? "max-h-[600px]" : "max-h-[110px]"}`}>
                    <ul className="space-y-2 text-sm">
                      {role.responsibilities.map((resp, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <span className="text-yellow-400 mt-0.5"><FontAwesomeIcon icon={faStar} /></span>
                          <div className="text-sm text-gray-100 leading-snug break-words">{resp}</div>
                        </li>
                      ))}
                      <li className="mt-2 text-xs text-gray-300">
                        <span className="font-medium">Interaction:</span> {role.interaction}
                      </li>
                    </ul>
                  </div>

                  {/* Footer buttons */}
                  {/* <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => handleCopyRole(role)}
                      className="flex-1 text-sm px-3 py-2 rounded-md bg-gray-800 border border-gray-700 hover:bg-gray-700"
                      title="Copy this role summary"
                    >
                      <FontAwesomeIcon icon={faCopy} className="mr-2" /> Copy
                    </button>

                    <button
                      onClick={() => {
                        const doc = new jsPDF();
                        doc.setFontSize(14);
                        doc.text(`${role.role} ${role.emoji}`, 12, 20);
                        doc.setFontSize(11);
                        doc.text(`Function: ${role.function}`, 12, 36);
                        doc.text("Responsibilities:", 12, 52);
                        role.responsibilities.forEach((r, i) => doc.text(`- ${r}`, 16, 68 + i * 12));
                        doc.text(`Interaction: ${role.interaction}`, 12, 68 + role.responsibilities.length * 12 + 12);
                        doc.save(`${role.role.replace(/\s+/g, "_").toLowerCase()}.pdf`);
                      }}
                      className="px-3 py-2 rounded-md bg-gray-800 border border-gray-700 hover:bg-gray-700 text-sm"
                      title="Export this role as PDF"
                    >
                      <FontAwesomeIcon icon={faDownload} className="mr-2" /> PDF
                    </button>
                  </div> */}
                </div>
              </div>

              {/* Small indicator when copied */}
              {/* {copied && (
                <div className="absolute top-2 right-2 bg-white bg-opacity-10 text-xs text-white px-2 py-1 rounded">
                  Copied ✓
                </div>
              )} */}
            </article>
          );
        })}
      </main>

      {/* Floating actions for mobile */}
      {/* <div className="fixed bottom-4 left-4 right-4 sm:bottom-6 sm:right-auto sm:left-auto sm:top-auto flex justify-center sm:justify-end z-40">
        <div className="flex gap-2 max-w-xl w-full sm:w-auto">
          <button
            onClick={handleExportPDF}
            className="flex-1 sm:flex-initial px-4 py-3 rounded-full bg-indigo-600 hover:bg-indigo-500 shadow-lg text-white text-sm flex items-center justify-center gap-2"
            title="Export full roles as PDF"
          >
            <FontAwesomeIcon icon={faDownload} /> Export PDF
          </button>
          <button
            onClick={() => {
              const allText = ecosystemRoles.map(r => `${r.role} — ${r.function} — ${r.responsibilities.join(", ")} — Interaction: ${r.interaction}`).join("\n\n");
              navigator.clipboard.writeText(allText);
              setCopied(true);
              setTimeout(() => setCopied(false), 2200);
            }}
            className="hidden sm:inline-flex px-4 py-3 rounded-full bg-gray-800 hover:bg-gray-700 shadow-lg text-white text-sm flex items-center justify-center gap-2"
            title="Copy all roles summary"
          >
            <FontAwesomeIcon icon={faCopy} /> Copy All
          </button>
        </div>
      </div> */}

      <div className="mt-6 text-center text-xs text-gray-500">
        Tap a card header to expand responsibilities. Use PDF/Copy to export or share with stakeholders.
      </div>
    </div>
  );
};

export default SHEETALThink5WhysRoles;
